import { useState, useEffect, useMemo, lazy, Suspense } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Annotation Portal React example components
import Sidenav from "examples/Sidenav";

// Annotation Portal React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Annotation Portal React routes
import { useTranslatedRoutes } from "routes";

// Annotation Portal React contexts
import { useMaterialUIController } from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

import { getUserInfo } from "services/authService";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { LanguageProvider, useLanguage } from "context/LanguageContext";
import { LoadingProvider } from "context/LoadingContext";
import { SessionProvider } from "context/SessionContext";
import GlobalLoader from "components/GlobalLoader";
import SessionExpiredDialog from "components/SessionExpiredDialog";
import { useTranslation } from "hooks/useTranslation";
import PageLoader from "components/PageLoader";
import ProtectedRoute from "components/ProtectedRoute";

// custom import
const Basic = lazy(() => import("layouts/authentication/sign-in"));

function AppContent() {
  const [controller] = useMaterialUIController();
  const { direction, layout, sidenavColor, transparentSidenav, whiteSidenav, darkMode } =
    controller;
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const translatedRoutes = useTranslatedRoutes();
  const { isReady: translationsReady } = useLanguage();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return (
          <Route
            exact
            path={route.route}
            element={<ProtectedRoute roles={route.roles}>{route.component}</ProtectedRoute>}
            key={route.key}
          />
        );
      }

      return null;
    });

  /* Every route is registered and ProtectedRoute decides per request, so a
     wrong-role or logged-out visit redirects instead of rendering nothing.
     filteredRoutes only drives what the Sidenav lists. */
  const user = getUserInfo();
  let filteredRoutes = [];
  if (user && "role" in user) {
    filteredRoutes = translatedRoutes.filter((route) => route.roles.includes(user.role));
  }

  // Dashboard pages show the logo inside DashboardNavbar itself; this fixed
  // overlay only fills in on pages without that navbar (e.g. auth pages).
  const prathamLogo =
    layout === "dashboard" ? null : (
      <>
        <MDBox
          component="img"
          src="https://annotations-india-storage.s3.ap-south-1.amazonaws.com/images/Logo_without_tagline_Black.png"
          alt="Pratham"
          position="fixed"
          top="12px"
          left="14px"
          height="54px"
          zIndex={1250}
        />
        <MDBox
          position="fixed"
          top="12px"
          left={0}
          right={0}
          height="54px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={1249}
          sx={{ pointerEvents: "none" }}
        >
          {/* Teal → blue gradient lettering, to match the sign-in background and button. */}
          {/* lineHeight + padding give the gradient room for marks above/below the line in
              Indian scripts (e.g. the े in Hindi), which were otherwise left uncoloured. */}
          <MDTypography
            variant="h4"
            fontWeight="bold"
            lineHeight={1.5}
            sx={{
              py: "0.1em",
              // Deeper teal → deeper blue so the title reads strongly on the light background.
              background: "linear-gradient(90deg, #0b5d56 0%, #1450b8 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              letterSpacing: "0.01em",
              // Larger on tablets and up; phones keep the smaller size so it clears the logo.
              fontSize: { xs: "1.5rem", sm: "1.85rem", md: "2.1rem" },
            }}
          >
            {/* Always English: the sign-in page is shown before a language is known, and
                must not switch to the user's language while login hands over. */}
            Annotation India
          </MDTypography>
        </MDBox>
      </>
    );

  // Hold the whole UI behind a loader until translations arrive, so raw keys never flash
  if (!translationsReady) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <PageLoader />
      </ThemeProvider>
    );
  }

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={themeRTL}>
        <CssBaseline />
        {prathamLogo}
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName={t("navbar.title", "Annotation India")}
              routes={filteredRoutes}
            ></Sidenav>
          </>
        )}
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Navigate to="/authentication/sign-in" replace />} />
            <Route path="/authentication/sign-in" element={<Basic />} />
            {getRoutes(translatedRoutes)}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {prathamLogo}
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName={t("navbar.title", "Annotation India")}
            routes={filteredRoutes}
          ></Sidenav>
        </>
      )}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Navigate to="/authentication/sign-in" replace />} />
          <Route path="/authentication/sign-in" element={<Basic />} />
          {getRoutes(translatedRoutes)}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <LoadingProvider>
        <SessionProvider>
          <GlobalLoader />
          <SessionExpiredDialog />
          <AppContent />
        </SessionProvider>
      </LoadingProvider>
    </LanguageProvider>
  );
}
